import { useTranslations } from "next-intl";
import { ApiDownloadButton } from "@/components/api-download-button";

type StudyNotesPanelsProps = {
  bodyHtml: string | null;
  bodyHtmlHi: string | null;
  /** H2: when given, each language offers "Download as PDF" (stamped with the student's name). */
  lessonId?: string;
};

const downloadClassName = "mt-2 text-sm text-[var(--text-heading)] underline";

const notesBodyClassName =
  "space-y-3 border-t border-[var(--border)] px-4 py-4 text-sm leading-relaxed text-[var(--text-primary)] [&_h1]:text-xl [&_h1]:font-semibold [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:text-base [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:my-1 [&_p]:my-2 [&_hr]:my-4 [&_hr]:border-[var(--border)] [&_code]:rounded [&_code]:bg-[var(--surface-3)] [&_code]:px-1";

const summaryClassName =
  "cursor-pointer select-none px-4 py-3 font-medium marker:content-none [&::-webkit-details-marker]:hidden";

const chevronClassName =
  "inline-block text-[var(--text-muted)] transition-transform group-open:rotate-90";

export function StudyNotesPanels({ bodyHtml, bodyHtmlHi, lessonId }: StudyNotesPanelsProps) {
  const t = useTranslations("player");
  const hasEn = Boolean(bodyHtml?.trim());
  const hasHi = Boolean(bodyHtmlHi?.trim());

  if (!hasEn && !hasHi) return null;

  return (
    <section data-testid="course-player-study-notes" className="space-y-3">
      <h2 className="text-lg font-medium">{t("studyNotes")}</h2>
      <p className="text-sm text-[var(--text-muted)]">{t("studyNotesExpandHint")}</p>

      {hasEn ? (
        <details
          data-testid="course-player-study-notes-en"
          className="group rounded-lg border border-[var(--border)]"
        >
          <summary className={summaryClassName}>
            <span className="inline-flex items-center gap-2">
              <span aria-hidden className={chevronClassName}>
                ▸
              </span>
              {t("studyNotesEnglish")}
            </span>
          </summary>
          <article
            className={notesBodyClassName}
            data-testid="course-player-text"
            dangerouslySetInnerHTML={{ __html: bodyHtml! }}
          />
        </details>
      ) : null}

      {hasEn && lessonId ? (
        <ApiDownloadButton
          path={`/v1/lessons/${lessonId}/notes-pdf?locale=en`}
          className={downloadClassName}
          data-testid="download-notes-pdf-en"
        >
          {t("downloadNotesPdfEnglish")}
        </ApiDownloadButton>
      ) : null}

      {hasHi ? (
        <details
          data-testid="course-player-study-notes-hi"
          className="group rounded-lg border border-[var(--border)]"
        >
          <summary className={summaryClassName}>
            <span className="inline-flex items-center gap-2">
              <span aria-hidden className={chevronClassName}>
                ▸
              </span>
              {t("studyNotesHindi")}
            </span>
          </summary>
          <article
            className={notesBodyClassName}
            data-testid="course-player-text-hi"
            lang="hi"
            dangerouslySetInnerHTML={{ __html: bodyHtmlHi! }}
          />
        </details>
      ) : null}

      {hasHi && lessonId ? (
        <ApiDownloadButton
          path={`/v1/lessons/${lessonId}/notes-pdf?locale=hi`}
          className={downloadClassName}
          data-testid="download-notes-pdf-hi"
        >
          {t("downloadNotesPdfHindi")}
        </ApiDownloadButton>
      ) : null}
    </section>
  );
}
